<template>
    <!--
        UI-24-1 — sustainability rate breakdown, shown under the order price block.
        - 1 sustainable room: a single aggregated block (unchanged).
        - >1 sustainable room: one zsh-green-label fieldset per room, so each room
          gets its own correct rate label ("Zero Room Rate" / "Standard Room Rate")
          and explainer instead of one merged "Room Rate" block.
        Own-product accommodation rooms only; the per-room block self-hides when the
        room has no priced rows. baseRate is DERIVED so each room's rows reconcile to
        that room's CLIENT total: baseRate + (added) - (omitted) = roomTotal.
    -->
    <!-- 1 sustainable room: single aggregated block (unchanged) -->
    <div
        v-if="breakdown && sustainableRoomCount <= 1"
        class="sustainability-price-breakdown mt-2"
        data-cn="SustainabilityPriceBreakdown"
    >
        <div class="text-subtitle-2 font-weight-bold mb-1">
            {{ $t('order_sustainability.price_breakdown') }}
        </div>
        <v-row no-gutters class="justify-space-between text-body-2 mb-1">
            <span>{{ $t('order_sustainability.' + breakdown.label) }}</span>
            <span>{{ $filters.priceFormat(breakdown.baseRate, breakdown.currency) }}</span>
        </v-row>
        <!-- Extra services: own rows between the Room Rate row and the explainer -->
        <v-row
            v-for="(row, i) in extraRows(breakdown)"
            :key="`ex-${i}`"
            no-gutters
            class="justify-space-between text-body-2"
        >
            <span>{{ row.name }}</span>
            <span>{{ $filters.priceFormat(row.amount, breakdown.currency) }}</span>
        </v-row>
        <div
            v-if="breakdown.subHeader"
            class="text-caption font-italic font-weight-medium text-grey-darken-1 mt-4 mb-1"
        >
            {{ $t('order_sustainability.' + breakdown.subHeader) }}
        </div>
        <v-row v-for="(row, i) in otherRows(breakdown)" :key="i" no-gutters class="justify-space-between text-body-2">
            <span>{{ row.name }}</span>
            <!-- All amenity rows show their price; omitted (Sustainable) rows are prefixed with a minus. -->
            <span
                ><template v-if="row.sign === '-'">− </template
                >{{ $filters.priceFormat(row.amount, breakdown.currency) }}</span
            >
        </v-row>
        <v-divider class="my-1" />
        <v-row no-gutters class="justify-space-between text-subtitle-2 font-weight-bold">
            <span>{{ $t('order_sustainability.total') }}</span>
            <span>{{ $filters.priceFormat(breakdown.total, breakdown.currency) }}</span>
        </v-row>
    </div>

    <!-- >1 sustainable room: one zsh-green-label fieldset per room -->
    <div
        v-else-if="roomBreakdowns.length"
        class="sustainability-price-breakdown mt-2"
        data-cn="SustainabilityPriceBreakdown"
    >
        <div class="text-subtitle-2 font-weight-bold mb-1">
            {{ $t('order_sustainability.price_breakdown') }}
        </div>
        <div v-for="(b, i) in roomBreakdowns" :key="i" class="sustainability-room-fieldset mb-2">
            <!-- Green label header: room rate label + derived base rate -->
            <div class="sustainability-room-fieldset__header d-flex align-center justify-space-between px-3 py-1">
                <span class="text-body-2 font-weight-bold">{{ $t('order_sustainability.' + b.label) }}</span>
                <span class="text-body-2 font-weight-bold">{{ $filters.priceFormat(b.baseRate, b.currency) }}</span>
            </div>
            <div class="px-1 pt-1">
                <!-- Extra services: own rows between the Room Rate header and the explainer -->
                <v-row
                    v-for="(row, ri) in extraRows(b)"
                    :key="`ex-${ri}`"
                    no-gutters
                    class="justify-space-between text-body-2"
                >
                    <span>{{ row.name }}</span>
                    <span>{{ $filters.priceFormat(row.amount, b.currency) }}</span>
                </v-row>
                <div
                    v-if="b.subHeader"
                    class="text-caption font-italic font-weight-medium text-grey-darken-1 mt-4 mb-1"
                >
                    {{ $t('order_sustainability.' + b.subHeader) }}
                </div>
                <v-row v-for="(row, ri) in otherRows(b)" :key="ri" no-gutters class="justify-space-between text-body-2">
                    <span>{{ row.name }}</span>
                    <span
                        ><template v-if="row.sign === '-'">− </template
                        >{{ $filters.priceFormat(row.amount, b.currency) }}</span
                    >
                </v-row>
                <v-divider class="my-1" />
                <v-row no-gutters class="justify-space-between text-subtitle-2 font-weight-bold">
                    <span>{{ $t('order_sustainability.total') }}</span>
                    <span>{{ $filters.priceFormat(b.total, b.currency) }}</span>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        getSustainableRooms,
        loadRoomTypeConfig,
        deriveBreakdown,
        deriveRoomBreakdown,
        serviceClientAmount,
    } from '~~/customization_layers/zsh/app/composables/useOrderRoomSustainability'

    const props = defineProps<{
        services?: any[]
        orderPrices?: any[]
    }>()

    const breakdown = ref<any>(null) // aggregated block — 1-room case
    const roomBreakdowns = ref<any[]>([]) // per-room fieldsets — >1-room case
    const sustainableRoomCount = ref(0)

    // Extra-service rows render in their own block (between Room Rate and the explainer);
    // amenity/omitted rows stay below the explainer.
    const extraRows = (b: any) => (b?.rows || []).filter((r: any) => r.kind === 'extra')
    const otherRows = (b: any) => (b?.rows || []).filter((r: any) => r.kind !== 'extra')

    onMounted(async () => {
        const rooms = getSustainableRooms(props.services)
        sustainableRoomCount.value = rooms.length
        if (!rooms.length) return

        // Sustainable rooms need their room-type config (omitted amenities); ZERO rooms
        // already carry their added amenities on the order.
        const configs = reactive<Record<string, any>>({})
        await Promise.all(
            rooms
                .filter(r => r.level === 'SUSTAINABLE')
                .map(async r => {
                    configs[`${r.hotelId}:${r.roomTypeId}`] = await loadRoomTypeConfig(r.hotelId, r.roomTypeId)
                })
        )

        if (rooms.length > 1) {
            // One green-label fieldset per room. roomAmenities/extraServices live per
            // service, so render one fieldset per zero service even if it has several rooms.
            const fallbackCurrency = props.orderPrices?.[0]?.currency || ''
            const seenZeroServices = new Set<any>()
            const list: any[] = []
            for (const room of rooms) {
                if (room.level === 'ZERO') {
                    if (seenZeroServices.has(room.service)) continue
                    seenZeroServices.add(room.service)
                }
                const ct = serviceClientAmount(room.service)
                const cfg = room.level === 'SUSTAINABLE' ? configs[`${room.hotelId}:${room.roomTypeId}`] : null
                const b = deriveRoomBreakdown(room, cfg, ct?.amount ?? 0, ct?.currency || fallbackCurrency)
                if (b) list.push(b)
            }
            roomBreakdowns.value = list
        } else {
            // Single room: unchanged aggregated block reconciled to the order total.
            const total = calcTotalPrice(props.orderPrices || [])
            const orderTotal =
                total && typeof total === 'object' ? Number((total as any).amount ?? 0) : Number(total ?? 0)
            const orderCurrency = (total && (total as any).currency) || props.orderPrices?.[0]?.currency || ''
            breakdown.value = deriveBreakdown(rooms, configs, orderTotal, orderCurrency)
        }
    })
</script>

<style scoped>
    .sustainability-price-breakdown {
        border-top: 1px solid rgba(0, 0, 0, 0.12);
        padding-top: 4px;
    }

    .sustainability-room-fieldset {
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 4px;
        overflow: hidden;
    }

    .sustainability-room-fieldset__header {
        background-color: var(--zsh-primary, #3699a4);
        color: #fff;
    }
</style>
